import BookingForm from "@/components/BookingForm";
import SharedNavbar from "@/components/SharedNavbar";
import { CheckCircle2, Warehouse, ArrowRight, Phone, Truck, Clock3, MapPin, Package, ShieldCheck } from "lucide-react";

const LOGO_URL =
  "https://skyagent-artifacts.skywork.ai/router/agent/2026-06-23/prod_agent_3a0812e8-cabc-4e60-9023-9eb878dbad11/ds_logo_transparent_1343324419e041449c280c0bfa45d1f3.png";

const PHOTO_WH1 = "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1080&q=85";
const PHOTO_WH2 = "https://images.unsplash.com/photo-1553413077-190dd305871c?w=1080&q=85";
const PHOTO_WH3 = "https://images.unsplash.com/photo-1672552226380-486fe900b322?w=1080&q=85";
const PHOTO_WH4 = "https://images.unsplash.com/photo-1664382953403-fc1ac77073a0?w=1080&q=85";

const warehouses = [
  {
    name: "Lager Solothurn",
    location: "Bielstrasse 20, 4500 Solothurn",
    size: "3'000 m²",
    badge: "Hauptlager",
    badgeColor: "bg-primary text-white",
    desc: "Unser Hauptlager in Solothurn bildet das Herzstück unserer Logistikoperationen. Mit einer Lagerfläche von 3'000 m² verfügen wir über ausreichend Kapazität, um grosse Warenmengen unserer Handelspartner professionell einzulagern, zu kommissionieren und termingerecht zu verteilen.",
    photos: [PHOTO_WH1, PHOTO_WH3],
    features: [
      "3'000 m² Gesamtfläche",
      "Hochregalanlage für optimale Raumnutzung",
      "Klimatisierter Lagerbereich",
      "Direktanbindung an die Hauptverkehrsachsen",
      "24/7 Videoüberwachung & Sicherheit",
      "Rampenanlage für Sattelzüge & LKW",
      "EDV-gesteuertes Lagerverwaltungssystem",
      "Kapazität für 250+ Möbellieferungen täglich",
    ],
    mapLink: "https://maps.google.com/?q=Bielstrasse+20+4500+Solothurn",
  },
  {
    name: "Lager Grancia",
    location: "Grancia, Tessin (CH)",
    size: "1'000 m²",
    badge: "Regionallager Tessin",
    badgeColor: "bg-slate-700 text-white",
    desc: "Unser Regionallager in Grancia im Kanton Tessin ermöglicht uns eine schnelle und effiziente Versorgung der italienischsprachigen Schweiz. Mit 1'000 m² Lagerfläche können wir Waren für den Grossraum Lugano, Locarno und das gesamte Tessin optimal lagern und verteilen.",
    photos: [PHOTO_WH2, PHOTO_WH4],
    features: [
      "1'000 m² Lagerfläche",
      "Strategische Lage im Tessin",
      "Schnelle Lieferung Lugano & Locarno",
      "Direktverbindung Mailand – Zürich (A2)",
      "Qualifiziertes lokales Team",
      "Tägliche Verbindungen zum Hauptlager Solothurn",
      "Optimal für Südschweiz & Grenzregionen",
      "Kurze Lieferzeiten im gesamten Tessin",
    ],
    mapLink: "https://maps.google.com/?q=Grancia+Tessin+Schweiz",
  },
];

export default function Logistik() {
  return (
    <div className="min-h-screen bg-background">
      <SharedNavbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 py-16 text-white">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="max-w-3xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-white/80">
              <Warehouse className="h-3.5 w-3.5 text-red-400" />
              Operative Logistik
            </div>
            <h1 className="text-4xl font-bold leading-tight sm:text-5xl">
              Zwei Lager — <br />
              <span className="text-red-400">ein starkes Netzwerk</span>
            </h1>
            <p className="mt-5 text-lg text-white/75 leading-relaxed">
              DS Logistik betreibt zwei strategisch gelegene Lagerzentren in der Schweiz: das Hauptlager in Solothurn mit 3'000 m² und das Regionallager in Grancia (Tessin) mit 1'000 m². Zusammen bilden sie die Basis für unsere täglichen Lieferungen in der ganzen Schweiz.
            </p>
            <div className="mt-6 flex flex-wrap gap-4 text-sm">
              <button
                onClick={() => { const el = document.getElementById("termin-anfragen"); if(el){ el.scrollIntoView({behavior:"smooth",block:"start"}); setTimeout(()=>{ const f = document.getElementById("booking-name"); if(f) f.focus(); }, 600); }}}
                className="inline-flex items-center gap-2 rounded-full bg-white text-slate-900 px-6 py-3 text-sm font-bold shadow-lg hover:-translate-y-px transition-all"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"/></svg>
                Jetzt anfragen
              </button>
              {[
                "4'000 m² Gesamtlagerfläche",
                "2 strategische Standorte",
                "Lieferung in 48h schweizweit",
                "58+ Verteilungspunkte",
              ].map((f) => (
                <div key={f} className="flex items-center gap-1.5 text-white/80">
                  <CheckCircle2 className="h-4 w-4 text-green-400" />
                  {f}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-b border-border/40 bg-primary/5 py-8">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
            {[
              { val: "4'000 m²", label: "Gesamtlagerfläche" },
              { val: "2",        label: "Lagerstandorte" },
              { val: "48h",      label: "Lieferzeit schweizweit" },
              { val: "250K+",    label: "Lieferungen pro Jahr" },
            ].map(({ val, label }) => (
              <div key={label} className="text-center">
                <p className="font-mono text-3xl font-bold text-primary">{val}</p>
                <p className="mt-1 text-sm text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warehouses */}
      <section className="py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 space-y-20">
          {warehouses.map((wh, idx) => (
            <div
              key={wh.name}
              className={`grid gap-10 lg:grid-cols-2 lg:items-start ${idx % 2 === 1 ? "lg:flex-row-reverse" : ""}`}
            >
              {/* Photos */}
              <div className={`grid gap-4 ${idx % 2 === 1 ? "lg:order-2" : ""}`}>
                <div
                  className="overflow-hidden rounded-3xl border border-border/40"
                  style={{ boxShadow: "0 20px 50px -24px rgba(37,10,8,0.25)" }}
                >
                  <img
                    src={wh.photos[0]}
                    alt={wh.name}
                    className="h-72 w-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="overflow-hidden rounded-2xl border border-border/40">
                    <img
                      src={wh.photos[1]}
                      alt={`${wh.name} Innenansicht`}
                      className="h-40 w-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>
                  {/* Size badge card */}
                  <div className="flex flex-col items-center justify-center rounded-2xl bg-primary/5 border border-primary/20 p-4">
                    <Warehouse className="h-8 w-8 text-primary mb-2" />
                    <p className="font-mono text-3xl font-bold text-primary">{wh.size}</p>
                    <p className="text-xs text-muted-foreground mt-1 text-center">Lagerfläche</p>
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className={idx % 2 === 1 ? "lg:order-1" : ""}>
                <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold mb-4 ${wh.badgeColor}`}>
                  {wh.badge}
                </span>
                <h2 className="text-3xl font-bold text-foreground sm:text-4xl">{wh.name}</h2>
                <a
                  href={wh.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  <MapPin className="h-4 w-4" /> {wh.location}
                </a>
                <p className="mt-5 text-muted-foreground leading-relaxed">{wh.desc}</p>

                <div className="mt-6 grid gap-2 sm:grid-cols-2">
                  {wh.features.map((f) => (
                    <div key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5 text-green-500" />
                      {f}
                    </div>
                  ))}
                </div>

                <a
                  href={wh.mapLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary/40 px-5 py-2.5 text-sm font-semibold text-primary hover:bg-primary hover:text-white transition-colors"
                >
                  <MapPin className="h-4 w-4" /> Auf Google Maps öffnen
                </a>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Was wir bieten */}
      <section className="py-16 bg-muted/30 border-y border-border/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">Leistungsangebot</p>
            <h2 className="mt-3 text-3xl font-bold text-foreground sm:text-4xl">Was wir für Sie lagern & verteilen</h2>
            <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
              Wir übernehmen die gesamte Lagerhaltung, Kommissionierung und Distribution — von der Annahme bis zur Haustüre Ihres Kunden.
            </p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: Package,      title: "Warehousing & Lagerung",        text: "Professionelle Einlagerung aller Möbel- und Haushaltswaren — sicher, trocken und klimatisiert." },
              { icon: Truck,        title: "Distribution & Auslieferung",    text: "Tägliche Touren in alle Kantone — vom Lager direkt zum Endkunden in 48 Stunden." },
              { icon: Clock3,       title: "Same-Day Kommissionierung",      text: "Bestelleingang heute — Kommissionierung und Verladung noch heute für morgige Lieferung." },
              { icon: ShieldCheck,  title: "Schadensschutz & Versicherung",  text: "Alle eingelagerten Waren sind vollständig versichert. Wir gehen sorgsam mit jedem Artikel um." },
              { icon: MapPin,       title: "B2B & Retail-Logistik",          text: "Direktbelieferung von Filialen, Showrooms und Lagern — für Retail-Partner und Onlinehändler." },
              { icon: Warehouse,    title: "Cross-Docking",                  text: "Effiziente Umschlagslösung ohne Zwischenlagerung — maximale Schnelligkeit, minimale Kosten." },
            ].map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="rounded-3xl border border-border/60 bg-card p-6"
                style={{ boxShadow: "0 12px 30px -18px rgba(37,10,8,0.16)" }}
              >
                <div className="inline-flex rounded-2xl bg-primary/10 p-3 text-primary mb-4">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="text-base font-bold text-foreground">{title}</h3>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      
      {/* Termin & Anfrage */}
      <section className="py-16 bg-muted/30 border-y border-border/40">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <div className="text-center mb-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">Jetzt buchen</p>
            <h2 className="mt-3 text-3xl font-bold text-foreground">Termin anfragen</h2>
            <p className="mt-3 text-muted-foreground">Wählen Sie Service, Datum und Uhrzeit — wir melden uns innerhalb von 24 Stunden.</p>
          </div>
          <BookingForm defaultService="Operative Logistik / Lager" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="mx-auto max-w-2xl px-5 lg:px-8 text-center">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-primary">Kontakt</p>
          <h2 className="mt-3 text-3xl font-bold text-foreground">Haben Sie Interesse an unseren Lagerdienstleistungen?</h2>
          <p className="mt-3 text-muted-foreground">
            Kontaktieren Sie uns für eine unverbindliche Beratung und ein massgeschneidertes Angebot.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <a
              href="tel:+41326712010"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-white shadow-[0_8px_24px_-10px_rgba(200,25,11,0.6)] hover:-translate-y-px transition-all"
            >
              <Phone className="h-4 w-4" /> 032 671 20 10
            </a>
            <a
              href="https://wa.me/41798883262"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-white hover:scale-105 transition-transform"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              WhatsApp
            </a>
            <a
              href={`mailto:andre.stalder@ds-logistik.ch?subject=${encodeURIComponent("Anfrage Lagerlogistik")}&body=${encodeURIComponent("Guten Tag,\n\nIch interessiere mich für Ihre Lagerlogistik-Dienstleistungen.\n\nBitte kontaktieren Sie mich für eine Beratung.\n\nFreundliche Grüsse")}`}
              className="inline-flex items-center gap-2 rounded-full border border-border/60 px-6 py-3 text-sm font-semibold hover:bg-muted transition-colors"
            >
              <ArrowRight className="h-4 w-4 text-primary" /> Per E-Mail anfragen
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/40 bg-background py-6 text-center">
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} DS Logistik · Bielstrasse 20, 4500 Solothurn ·{" "}
          <a href="/#/impressum" className="hover:text-primary">Impressum</a>
        </p>
      </footer>

      {/* WhatsApp floating */}
      <a
        href="https://wa.me/41798883262"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-white shadow-lg hover:scale-105 transition-transform"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6 fill-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
        <span className="text-sm font-bold">WhatsApp</span>
      </a>
    </div>
  );
}
